import * as React from 'react';
import { Prompt } from 'react-router-dom';
import { isEqual } from 'lodash';

import { Search, Button, Icons, Checkbox, Pagination } from 'src/components/';
import AdminNav from 'src/views/Admin/components/AdminNav';
import TableRow, { ITableRowValue } from 'src/views/Admin/components/TableRow';
import Container from 'src/components/Container';

interface ISubpageProps {
  currentPath: string;
  NewForm?: any;
  EditForm: any;
  columns: ITableRowValue[];
  getColumnValues: (item: any) => ITableRowValue[];
  getSearchString: (item: any) => string;
  items: any[];
  isLoading: boolean;
  archivedItems?: any[];
  hasArchive?: boolean;
  children?: React.ReactNode;
}

interface ISubpageState {
  newOpen: boolean;
  newClosable: boolean;
  filteredItems: any[];
  visibleItems: any[];
  searchTerm: string;
  dirtyEditForms: number[];
  currentPage: number;
  pageCount: number;
  isArchive?: boolean;
}

export default class Subpage extends React.Component<
  ISubpageProps,
  ISubpageState
> {
  ITEMS_PER_PAGE = 10;
  state: ISubpageState = {
    newOpen: false,
    newClosable: true,
    filteredItems: [],
    visibleItems: [],
    searchTerm: '',
    dirtyEditForms: [],
    currentPage: 1,
    pageCount: 1,
    isArchive: false,
  };

  componentDidUpdate(prevProps: ISubpageProps) {
    if (
      !isEqual(this.props.items, prevProps.items) ||
      !isEqual(this.props.archivedItems, prevProps.archivedItems)
    ) {
      this.filterItems();
    }
  }

  filterItems = (term?: string) => {
    this.setState(
      (state) => {
        const isArchive =
          this.props.archivedItems && this.props.archivedItems.length === 0
            ? false
            : state.isArchive;
        const searchTerm = term !== undefined ? term : state.searchTerm;
        const items =
          this.props.archivedItems && isArchive
            ? this.props.archivedItems
            : this.props.items;
        const isEmptySearch = searchTerm === '';
        const filteredItems = isEmptySearch
          ? items
          : items.filter(
              (item: any) =>
                this.props
                  .getSearchString(item)
                  .toLowerCase()
                  .indexOf(searchTerm) !== -1
            );
        const pageCount =
          Math.ceil(filteredItems.length / this.ITEMS_PER_PAGE) || 1;
        const currentPage =
          state.currentPage > pageCount ? 1 : state.currentPage;

        return {
          isArchive,
          searchTerm,
          filteredItems,
          currentPage,
          pageCount,
        };
      },
      () => void this.setPage(this.state.currentPage)
    );
  };

  toggleArchive = () => {
    if (!this.props.hasArchive) {
      return;
    }
    this.setState(
      (state) => ({
        isArchive: !state.isArchive,
      }),
      this.filterItems
    );
  };

  toggleNewOpen = (noCheck?: boolean) => {
    if (
      !noCheck &&
      !this.state.newClosable &&
      !window.confirm('Your input will be lost. Close?')
    ) {
      return;
    }
    this.setState((state) => ({
      newOpen: !state.newOpen,
      newClosable: true,
    }));
  };

  setPage = (page: number) => {
    const startIndex = (page - 1) * this.ITEMS_PER_PAGE;
    this.setState((state) => ({
      visibleItems: state.filteredItems.slice(
        startIndex,
        startIndex + this.ITEMS_PER_PAGE
      ),
      currentPage: page,
    }));
  };

  setNewClosable = (dirty: boolean) => {
    this.setState({ newClosable: !dirty });
  };

  setEditFormDirty = (id: number) => (dirty: boolean) => {
    this.setState((state: ISubpageState) => {
      const existsInState = state.dirtyEditForms.includes(id);
      if (existsInState && !dirty) {
        return {
          dirtyEditForms: state.dirtyEditForms.filter(
            (formId) => formId !== id
          ),
        };
      }
      if (!existsInState && dirty) {
        return {
          dirtyEditForms: [...state.dirtyEditForms, id],
        };
      }
      return state;
    });
  };

  render() {
    const {
      visibleItems,
      newOpen,
      newClosable,
      dirtyEditForms,
      currentPage,
      pageCount,
      isArchive,
    } = this.state;
    const {
      currentPath,
      NewForm,
      EditForm,
      columns,
      getColumnValues,
      isLoading,
      hasArchive,
      archivedItems,
      children,
    } = this.props;

    return (
      <Container>
        <Prompt
          when={!newClosable}
          message="Your input will be lost. Leave page?"
        />
        <Prompt
          when={dirtyEditForms.length > 0}
          message="Unsaved changes in a form. Leave page?"
        />
        <div className="flex bg-grey py-12">
          <AdminNav current={currentPath} />

          <div className="flex-1 px-16">
            <div className="flex justify-between items-center pr-16 mb-8">
              {NewForm && (
                <Button
                  background="grey"
                  size="small"
                  onClick={this.toggleNewOpen.bind(null, false)}
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {newOpen ? 'Cancel' : 'Add new'}
                </Button>
              )}
              <Pagination
                className="flex-1"
                current={currentPage}
                count={pageCount}
                onChange={this.setPage}
              />
              <div className="flex items-center">
                {children}
                {hasArchive && (
                  <Checkbox
                    name="archived"
                    className="mr-10"
                    checked={isArchive}
                    disabled={archivedItems && archivedItems.length === 0}
                    onChange={this.toggleArchive}
                  >
                    Archived ({archivedItems && archivedItems.length})
                  </Checkbox>
                )}
                <Search onChange={this.filterItems} />
              </div>
            </div>

            {newOpen && NewForm && (
              <div className="mb-8 shadow-lg border-t-3 border-grey-dark">
                <NewForm
                  preventClosing={this.setNewClosable}
                  close={this.toggleNewOpen}
                />
              </div>
            )}

            <TableRow heading={true} values={columns} />
            {isLoading ? (
              <div className="bg-white loader loader-white h-128" />
            ) : visibleItems.length === 0 ? (
              <div className="bg-white px-3 py-4">
                <h2>
                  <Icons.Circle
                    character="!"
                    color="secondary"
                    bgFill={true}
                    className="mb-2 mr-2"
                  />{' '}
                  No items found
                </h2>
              </div>
            ) : (
              visibleItems.map((item: any, index) => (
                <TableRow
                  key={item.id || index}
                  values={getColumnValues(item)}
                  onDirtyChange={this.setEditFormDirty(item.id)}
                >
                  {(rowProps) => (
                    <EditForm item={item} isArchive={isArchive} {...rowProps} />
                  )}
                </TableRow>
              ))
            )}

            <Pagination
              className="mt-4"
              current={currentPage}
              count={pageCount}
              onChange={this.setPage}
            />
          </div>
        </div>
      </Container>
    );
  }
}



// WEBPACK FOOTER //
// ./src/views/Admin/components/Subpage.tsx